import type { ConsolePermissionKey } from '@sentinel/shared';
import { Icon, type IconName } from '@sentinel/ui';
import { useAuthStore } from '@sentinel/web-core';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

interface Section {
  key: string;
  to: '/' | '/requests' | '/support' | '/team' | '/audit' | '/settings';
  icon: IconName;
  /** Any of these console permissions shows the section. */
  anyOf: readonly ConsolePermissionKey[];
}

const SECTIONS: readonly Section[] = [
  { key: 'organizations', to: '/', icon: 'building', anyOf: ['console.organizations:view'] },
  { key: 'requests', to: '/requests', icon: 'bell', anyOf: ['console.entitlements:view'] },
  { key: 'support', to: '/support', icon: 'wrench', anyOf: ['console.support:request'] },
  { key: 'team', to: '/team', icon: 'teams', anyOf: ['console.members:view'] },
  {
    key: 'audit',
    to: '/audit',
    icon: 'timeline',
    anyOf: ['console.audit:view', 'console.audit:view_own'],
  },
  { key: 'settings', to: '/settings', icon: 'settings', anyOf: ['console.settings:manage'] },
];

/** The console sections the platform role may open. */
export function ConsoleNav() {
  const { t } = useTranslation();
  const held = useAuthStore((s) => s.me?.consolePermissions ?? []);
  const visible = SECTIONS.filter((s) => s.anyOf.some((p) => held.includes(p)));
  return (
    <nav className="nav" aria-label={t('console.nav.label')}>
      {visible.map((s) => (
        <Link
          key={s.key}
          to={s.to}
          activeOptions={{ exact: s.to === '/' }}
          activeProps={{ 'aria-current': 'page' }}
        >
          <Icon name={s.icon} size={16} />
          {t(`console.nav.${s.key}`)}
        </Link>
      ))}
    </nav>
  );
}
